import type { NavSectionProps } from 'src/components/nav-section';

import { paths } from 'src/routes/paths';

import { CONFIG } from 'src/global-config';

import { Iconify } from 'src/components/iconify';
import { SvgColor } from 'src/components/svg-color';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`${CONFIG.assetsDir}/assets/icons/navbar/${name}.svg`} />
);

const ICONS = {
  job: icon('ic-job'),
  blog: icon('ic-blog'),
  chat: icon('ic-chat'),
  mail: icon('ic-mail'),
  user: icon('ic-user'),
  file: icon('ic-file'),
  lock: icon('ic-lock'),
  tour: icon('ic-tour'),
  order: icon('ic-order'),
  label: icon('ic-label'),
  blank: icon('ic-blank'),
  kanban: icon('ic-kanban'),
  folder: icon('ic-folder'),
  course: icon('ic-course'),
  params: icon('ic-params'),
  banking: icon('ic-banking'),
  booking: icon('ic-booking'),
  invoice: icon('ic-invoice'),
  product: icon('ic-product'),
  calendar: icon('ic-calendar'),
  disabled: icon('ic-disabled'),
  external: icon('ic-external'),
  subpaths: icon('ic-subpaths'),
  menuItem: icon('ic-menu-item'),
  ecommerce: icon('ic-ecommerce'),
  analytics: icon('ic-analytics'),
  dashboard: icon('ic-dashboard'),
};

// ----------------------------------------------------------------------

/**
 * Input nav data is an array of navigation section items used to define the structure and content of a navigation bar.
 * Each section contains a subheader and an array of items, which can include nested children items.
 *
 * Each item can have the following properties:
 * - `title`: The title of the navigation item.
 * - `path`: The URL path the item links to.
 * - `icon`: An optional icon component to display alongside the title.
 * - `info`: Optional additional information to display, such as a label.
 * - `allowedRoles`: An optional array of roles that are allowed to see the item.
 * - `caption`: An optional caption to display below the title.
 * - `children`: An optional array of nested navigation items.
 * - `disabled`: An optional boolean to disable the item.
 * - `deepMatch`: An optional boolean to indicate if the item should match subpaths.
 */
export const navData = (t: (key: string) => string): NavSectionProps['data'] => [
  /**
   * Overview
   */
  {
    subheader: t('user.overview'),
    // Order matters: splitNavData() takes the first MAX_VISIBLE_TABS `primary`
    // items, in order, as the bottom bar - and useDefaultDashboardPath() resolves
    // `/dashboard` from the FIRST one. Home must stay first so the dealer
    // overview keeps rendering at /dashboard; app launch is routed separately by
    // useAppLaunchPath().
    items: [
      { title: t('user.home'), path: paths.dashboard.root, icon: ICONS.dashboard, primary: true },
      {
        title: t('user.sendMoney'),
        path: paths.dashboard.artham.send,
        icon: ICONS.external,
        primary: true,
      },
      {
        title: t('user.senders'),
        path: paths.dashboard.artham.senders,
        icon: <Iconify icon="solar:users-group-rounded-bold-duotone" width={24} />,
        primary: true,
      },
      { title: t('user.history'), path: paths.dashboard.artham.history, icon: ICONS.file, primary: true },
      {
        title: t('user.credit'),
        path: paths.dashboard.artham.credit,
        icon: <Iconify icon="solar:wallet-money-bold-duotone" width={24} />,
      },
      { title: t('user.earnings'), path: paths.dashboard.artham.earnings, icon: ICONS.banking },
      {
        title: t('user.myTickets'),
        path: paths.dashboard.artham.grievances.root,
        icon: <Iconify icon="solar:chat-round-dots-bold-duotone" width={24} />,
      },
      {
        title: t('user.kyc'),
        path: paths.dashboard.artham.kyc.root,
        icon: ICONS.lock,
      },
      {
        title: t('user.profile'),
        path: paths.dashboard.artham.profile,
        icon: ICONS.user,
      },
    ],
  },
];
